import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/App";
import GlassCard from "@/components/GlassCard";
import { useNavigate } from "react-router-dom";
import {
  Share2, Twitter, Facebook, Copy, Download, ArrowLeft,
  Flame, Activity, Heart, Moon, Shield, Dumbbell, Clock,
  Sparkles, Zap, Trophy, CheckCircle
} from "lucide-react";
import { toast } from "sonner";

const ICON_MAP = {
  flame: Flame, activity: Activity, heart: Heart, moon: Moon,
  shield: Shield, dumbbell: Dumbbell, clock: Clock,
  sparkles: Sparkles, zap: Zap
};

const TIER_COLORS = {
  bronze: { bg: "bg-orange-500/15", border: "border-orange-500/30", text: "text-orange-400", glow: "rgba(249,115,22,0.3)" },
  silver: { bg: "bg-slate-300/15", border: "border-slate-300/30", text: "text-slate-300", glow: "rgba(203,213,225,0.3)" },
  gold: { bg: "bg-yellow-400/15", border: "border-yellow-400/30", text: "text-yellow-400", glow: "rgba(250,204,21,0.4)" }
};

function ScoreCircle({ score }) {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="relative score-circle" style={{ width: 128, height: 128 }}>
      <svg width={128} height={128} className="-rotate-90">
        <circle cx={64} cy={64} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
        <circle cx={64} cy={64} r={radius} fill="none" stroke="#00f260" strokeWidth="8"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-4xl font-bold text-white neon-text">{score}</span>
        <span className="text-[9px] text-gray-400 uppercase tracking-wider">Longevity</span>
      </div>
    </div>
  );
}

export default function SharePage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [card, setCard] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      api().get("/share/card"),
      api().get("/achievements")
    ]).then(([cardRes, achRes]) => {
      setCard(cardRes.data);
      setAchievements(achRes.data.achievements || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api]);

  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const shareText = card
    ? `Athlevity Teljesítmény | Longevity: ${card.longevity_score}/100 | Bio-kor: ${card.biological_age} | VO2max: ${card.vo2max} | ${card.active_streak} napos sorozat`
    : "";

  const copyToClipboard = () => {
    navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
    setCopied(true);
    toast.success("Másolva a vágólapra!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  };

  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, "_blank");
  };

  const downloadCard = async () => {
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#1a1a2e",
        scale: 2
      });
      const link = document.createElement("a");
      link.download = `athlevity-${card.user_name}-${card.date}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Kártya letöltve!");
    } catch {
      toast.error("A letöltéshez html2canvas szükséges. Használd a képernyőkép funkciót!");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 md:pt-20 max-w-2xl mx-auto" data-testid="share-page">
      {/* Header */}
      <div className="flex items-center gap-4 fade-in-up">
        <button
          data-testid="share-back-btn"
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <ArrowLeft size={18} className="text-white" />
        </button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Megosztás</h1>
          <p className="text-sm text-gray-400">Oszd meg teljesítményed a világgal</p>
        </div>
        <Share2 className="text-[#00f260] ml-auto" size={24} />
      </div>

      {/* Share Card Preview */}
      <div ref={cardRef}>
        <div
          className="relative overflow-hidden rounded-2xl border border-white/10 p-8 fade-in-up fade-in-up-1"
          style={{
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #1a1a2e 100%)"
          }}
          data-testid="share-card-preview"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-[#00f260]/5 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-[#0f3460]/20 blur-2xl" />

          {/* Header */}
          <div className="flex items-center justify-between mb-6 relative z-10">
            <div>
              <p className="font-heading text-xs text-[#00f260] uppercase tracking-widest mb-1">Athlevity</p>
              <h2 className="font-heading text-2xl font-bold text-white">{card?.user_name}</h2>
              <p className="text-xs text-gray-400">{card?.sport} &middot; {card?.date}</p>
            </div>
            <ScoreCircle score={card?.longevity_score || 87} />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 relative z-10">
            <StatBox label="Bio-kor" value={`${card?.biological_age}`} unit="év" color="#00f260" />
            <StatBox label="VO2max" value={`${card?.vo2max}`} unit="ml/kg/min" color="#00f260" />
            <StatBox label="Avg HRV" value={`${card?.avg_hrv}`} unit="ms" color="#ffc107" />
            <StatBox label="Avg alvás" value={`${card?.avg_sleep_hours}`} unit="óra" color="#64b5f6" />
          </div>

          {/* Bottom stats */}
          <div className="flex items-center gap-6 relative z-10">
            <div className="flex items-center gap-2">
              <Flame size={16} className="text-[#00f260]" />
              <span className="text-sm text-white font-medium">{card?.active_streak} nap</span>
              <span className="text-xs text-gray-400">sorozat</span>
            </div>
            <div className="flex items-center gap-2">
              <Dumbbell size={16} className="text-[#00f260]" />
              <span className="text-sm text-white font-medium">{card?.weekly_workout_count} edzés</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-[#00f260]" />
              <span className="text-sm text-white font-medium">{card?.total_workout_min} perc</span>
            </div>
          </div>

          {/* Branding */}
          <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between relative z-10">
            <span className="font-heading text-xs text-gray-500 tracking-widest">PERFORMANCE &middot; LONGEVITY &middot; LIFE</span>
            <span className="font-heading text-xs text-[#00f260] font-bold tracking-wider">Athlevity</span>
          </div>
        </div>
      </div>

      {/* Achievements */}
      {achievements.length > 0 && (
        <div className="space-y-3 fade-in-up fade-in-up-2">
          <div className="flex items-center gap-2">
            <Trophy size={16} className="text-[#00f260]" />
            <p className="text-xs text-gray-400 uppercase tracking-wider">Eredmények ({achievements.length})</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {achievements.map((ach) => {
              const Icon = ICON_MAP[ach.icon] || Trophy;
              const tier = TIER_COLORS[ach.tier] || TIER_COLORS.bronze;
              return (
                <div
                  key={ach.id}
                  data-testid={`achievement-${ach.id}`}
                  className={`flex items-center gap-3 p-4 rounded-xl border ${tier.bg} ${tier.border} transition-all hover:scale-[1.02]`}
                  style={{ boxShadow: `0 0 15px ${tier.glow}` }}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tier.bg}`}>
                    <Icon size={20} className={tier.text} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${tier.text}`}>{ach.name}</p>
                    <p className="text-xs text-gray-400 truncate">{ach.desc}</p>
                  </div>
                  <span className="text-[10px] text-gray-500 uppercase font-heading tracking-wider">{ach.tier}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Share Actions */}
      <div className="space-y-3 fade-in-up fade-in-up-3">
        <p className="text-xs text-gray-400 uppercase tracking-wider">Megosztás módja</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ShareButton
            testId="share-twitter-btn"
            icon={Twitter}
            label="Twitter / X"
            color="#1DA1F2"
            onClick={shareTwitter}
          />
          <ShareButton
            testId="share-facebook-btn"
            icon={Facebook}
            label="Facebook"
            color="#4267B2"
            onClick={shareFacebook}
          />
          <ShareButton
            testId="share-copy-btn"
            icon={copied ? CheckCircle : Copy}
            label={copied ? "Másolva!" : "Link másolás"}
            color="#00f260"
            onClick={copyToClipboard}
          />
          <ShareButton
            testId="share-download-btn"
            icon={Download}
            label="Letöltés"
            color="#ffc107"
            onClick={downloadCard}
          />
        </div>
      </div>

      {/* Motivational text */}
      <GlassCard className="text-center py-6 fade-in-up fade-in-up-4" testId="share-motivation-card">
        <p className="text-sm text-white/80 leading-relaxed mb-2">
          Inspiráld másokat a te teljesítményeddel!
        </p>
        <p className="text-xs text-gray-400">
          Minden megosztás motiválhat valakit, hogy elkezdje az egészséges életmódot.
        </p>
      </GlassCard>
    </div>
  );
}

function StatBox({ label, value, unit, color }) {
  return (
    <div className="bg-white/5 rounded-xl p-3 text-center border border-white/5">
      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="font-heading text-xl font-bold" style={{ color }}>{value}</p>
      <p className="text-[10px] text-gray-500">{unit}</p>
    </div>
  );
}

function ShareButton({ icon: Icon, label, color, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all hover:scale-105 active:scale-95"
    >
      <Icon size={22} style={{ color }} />
      <span className="text-xs text-gray-400">{label}</span>
    </button>
  );
}
